// Query-string sync for the calendar's filters.
//
// Why this exists: almost nobody discovers the filter drawer on their own (60 days
// of PostHog: 6 type-toggles by 3 people). But a PRE-filtered link does that work
// for the reader. So `?hosts=live-music&cost=free` can be pasted into a Reddit or
// Facebook reply and land someone straight on the events they asked about.
//
// Two rules this file exists to enforce:
//
//   1. NEVER drop query params we don't own. fbclid / utm_* / ref carry the
//      attribution that tells us which reply actually drove the visit, and every
//      write here starts from the CURRENT params rather than a blank set.
//
//   2. An unknown host slug degrades to "no host filter", never to an empty
//      calendar. These links live forever in public comments, so a stale one
//      (host renamed, category retired) must show everything rather than nothing.
//
// Deliberately NOT synced: the anchor date. A link shared in a comment should open
// on today whenever it's clicked, not on the week it was created.
//
// One more param lives here that isn't part of UrlFilters/readUrlFilters/
// writeUrlFilters above: `event`, for a link that opens straight into one
// event's modal (?event=<id>). It's a single id rather than a set of
// checkboxes, and its open/close lifecycle belongs to CalendarView's own
// modal state rather than the filter drawer, so it gets its own small
// read/write pair near the bottom of this file instead of folding into the
// functions above. The same two rules still apply: an unknown id shows the
// normal page rather than an error, and writing it never disturbs any other
// param, this one included.
//
// `q` works the same way (?q=mahjong): the search box's text, read once on load
// and written back as the visitor types, so a search can be shared as a link and
// a replay's URL shows what was searched. Its own pair for the same reason as
// `event`: it changes per keystroke and has nothing to do with the drawer.

import type { CostFilter, ViewMode } from '../types'

const HOSTS = 'hosts'
const HOSTED = 'hosted'
const COST = 'cost'
const VIEW = 'view'
const GROUP = 'group'
// Location boxes, e.g. ?area=bedford or ?area=downtown,rivermont (Andrew, 2026-09-27).
const AREA = 'area'
const EVENT = 'event'
const QUERY = 'q'

const COSTS: string[] = ['all', 'free', 'ticketed', 'promo']
// Every mode the app can render. 'rolling' was missing here once, so a
// ?view=rolling link we had written ourselves was silently dropped on the way in.
const VIEWS: string[] = ['3day', 'rolling', 'month', 'agenda']

// Retired modes and what they become. 'week' was the fixed Sun-Sat view until
// 7/28; mobile visits wrote ?view=week all through launch week, so those links
// are sitting in Facebook and Reddit comments right now. They open on the 7-day
// span instead of being dropped back to the default, which is the closest thing
// left and keeps a shared link pointing at what it meant.
const RETIRED_VIEWS: Record<string, ViewMode> = { week: 'rolling' }

// Omitted from the query string, because it's what a plain visit already shows.
const DEFAULT_VIEW = '3day'

// Retired CATEGORIES and where their links should land, the same idea as
// RETIRED_VIEWS above and for the same reason: these slugs are sitting in
// Facebook and Reddit comments right now, and the categories they name stopped
// existing after they were posted.
//
// Rule 2 at the top of this file already stops a stale slug emptying the
// calendar - it drops, and the reader gets everything. That is the safe failure,
// not the right one: somebody clicked a link that promised ghost tours or wine
// nights and got the unfiltered calendar to sift through. An alias sends them to
// the category the events actually moved to.
//
// Keyed by SLUG on both sides, so this reads against the URL rather than against
// the column, and resolveHostSlugs still checks the target really exists before
// applying it. A category that gets retired without a successor belongs nowhere
// in here - dropping is right when there is no honest destination.
//
//   wine-drinks    -> Food & Drink       absorbed it (7/29 taxonomy, task #75)
//   class-workshop -> Art & Craft        where its craft rows went, same change
//   watch-party    -> Sports             folded there 7/28 (task #60)
const RETIRED_HOST_SLUGS: Record<string, string> = {
  'wine-drinks': 'food-drink',
  'class-workshop': 'art-craft',
  'watch-party': 'sports',
}

/** "Kids & Family" -> "kids-family". Keeps a pasted link readable. */
export function slugify(host: string): string {
  return host
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export interface UrlFilters {
  /** Slugs as they appeared in the URL; resolved to real hosts once events load. */
  hostSlugs: string[]
  /** Hosted-by (organiser) slugs, e.g. ?hosted=the-trailhead. Same lifecycle as hostSlugs. */
  hostedBySlugs: string[]
  cost: CostFilter
  /** null when absent or invalid, so the caller keeps its own default. */
  view: ViewMode | null
  /** A named group from lyh_filter_groups, e.g. ?group=anime. */
  group: string | null
  /** Location slugs, e.g. ?area=bedford. Resolved against AREAS by the caller. */
  areaSlugs: string[]
}

export function readUrlFilters(): UrlFilters {
  const p = new URLSearchParams(window.location.search)

  const hostSlugs = (p.get(HOSTS) ?? '')
    .split(',')
    .map((s) => slugify(s))
    .filter(Boolean)

  const hostedBySlugs = (p.get(HOSTED) ?? '')
    .split(',')
    .map((s) => slugify(s))
    .filter(Boolean)

  const rawCost = (p.get(COST) ?? '').toLowerCase()
  const cost = (COSTS.includes(rawCost) ? rawCost : 'all') as CostFilter

  const rawView = (p.get(VIEW) ?? '').toLowerCase()
  const view =
    RETIRED_VIEWS[rawView] ?? ((VIEWS.includes(rawView) ? rawView : null) as ViewMode | null)

  const group = slugify(p.get(GROUP) ?? '') || null

  const areaSlugs = (p.get(AREA) ?? '')
    .split(',')
    .map((s) => slugify(s))
    .filter(Boolean)

  return { hostSlugs, hostedBySlugs, cost, view, group, areaSlugs }
}

/**
 * Match URL slugs against the hosts that actually exist, dropping any that don't.
 * Order follows the URL, and duplicates collapse.
 *
 * A retired slug is tried against its successor before being dropped, so an old
 * ?hosts=wine-drinks link lands on Food & Drink rather than on the whole
 * calendar. The successor is looked up in the same map as everything else, which
 * means an alias pointing at a category that has ALSO since gone away drops the
 * way any unknown slug does instead of resolving to nothing.
 */
export function resolveHostSlugs(slugs: string[], allHosts: string[]): string[] {
  const bySlug = new Map(allHosts.map((h) => [slugify(h), h]))
  const out: string[] = []
  for (const s of slugs) {
    const successor = RETIRED_HOST_SLUGS[s]
    const host = bySlug.get(s) ?? (successor ? bySlug.get(successor) : undefined)
    if (host && !out.includes(host)) out.push(host)
  }
  return out
}

/**
 * Hosted-by version of resolveHostSlugs: same drop-unknowns rule, no retired
 * map (organiser names come from lib/hostedBy's canonicalization, and when one
 * changes there the old link degrades to "everyone" per rule 2 rather than
 * needing an alias here - the alias belongs in hostedBy's own ALIASES table).
 *
 * Resolve against EVERY name the events can produce, not the drawer's list: the
 * drawer thresholds at 3+ rows, but a link for a two-event organiser (the
 * Forest Farmers Market case in lib/hostedBy) should still land filtered.
 */
export function resolveHostedBySlugs(slugs: string[], allNames: string[]): string[] {
  const bySlug = new Map(allNames.map((n) => [slugify(n), n]))
  const out: string[] = []
  for (const s of slugs) {
    const name = bySlug.get(s)
    if (name && !out.includes(name)) out.push(name)
  }
  return out
}

/**
 * Push the current filter state into the query string. Defaults (no hosts, cost
 * "all", the 3-day view) are omitted so a plain visit keeps a clean URL and a
 * shared link only carries what it actually changes.
 */
export function writeUrlFilters(f: {
  hostSlugs: string[]
  hostedBySlugs: string[]
  cost: CostFilter
  view: ViewMode
  group?: string | null
  areaSlugs?: string[]
}): void {
  // Start from the live params so fbclid / utm_* / ref survive untouched.
  const p = new URLSearchParams(window.location.search)

  if (f.group) p.set(GROUP, f.group)
  else p.delete(GROUP)

  if (f.hostSlugs.length > 0) p.set(HOSTS, f.hostSlugs.join(','))
  else p.delete(HOSTS)

  if (f.hostedBySlugs.length > 0) p.set(HOSTED, f.hostedBySlugs.join(','))
  else p.delete(HOSTED)

  if (f.cost !== 'all') p.set(COST, f.cost)
  else p.delete(COST)

  if (f.areaSlugs && f.areaSlugs.length > 0) p.set(AREA, f.areaSlugs.join(','))
  else p.delete(AREA)

  if (f.view !== DEFAULT_VIEW) p.set(VIEW, f.view)
  else p.delete(VIEW)

  // toString() percent-encodes the host separator to %2C. A comma is a legal query
  // character and these links get pasted into public comments, so keep it readable.
  // Safe to decode: params split on & and =, never on commas.
  const qs = p.toString().replace(/%2C/g, ',')
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`

  // replaceState, not pushState: one history entry per checkbox would bury the
  // back button and trap people on the page.
  window.history.replaceState(null, '', next)
}

/**
 * The event id from `?event=<id>`, or null when absent.
 *
 * Read-only, and deliberately not validated here: knowing whether the id is
 * real needs the loaded event rows plus a past/future check, and this file
 * never sees either. The caller (App.tsx) resolves it against the events it
 * has loaded and calls writeEventId once it knows the outcome - degrading to
 * "open nothing" for an id that turns out to be unknown or past, the same
 * "look up once there's data, fail quiet otherwise" shape resolveHostSlugs
 * uses above, just resolved by the caller instead of in here.
 */
export function readEventId(): string | null {
  const p = new URLSearchParams(window.location.search)
  return p.get(EVENT) || null
}

/**
 * Set or clear `?event=` in place, preserving every other param, including
 * ones we don't own (fbclid/utm_*) and the filter params writeUrlFilters
 * owns. Mirrors that function's replaceState/comma-decoding pattern but
 * stands alone: the event modal's open/close lifecycle is driven from
 * CalendarView, which never otherwise touches the query string.
 */
export function writeEventId(id: string | null): void {
  const p = new URLSearchParams(window.location.search)
  if (id) p.set(EVENT, id)
  else p.delete(EVENT)

  const qs = p.toString().replace(/%2C/g, ',')
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
  window.history.replaceState(null, '', next)
}

/** The search box's text from `?q=`, or '' when absent. */
export function readSearchQuery(): string {
  const p = new URLSearchParams(window.location.search)
  return (p.get(QUERY) ?? '').trim()
}

/**
 * Set or clear `?q=` in place, preserving every other param. Empty or
 * whitespace-only text removes it, so clearing the search box leaves a clean
 * URL. Same replaceState and comma handling as writeEventId.
 */
export function writeSearchQuery(q: string): void {
  const p = new URLSearchParams(window.location.search)
  const text = q.trim()
  if (text) p.set(QUERY, text)
  else p.delete(QUERY)

  const qs = p.toString().replace(/%2C/g, ',')
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
  if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    window.history.replaceState(null, '', next)
  }
}
